/**
 * IntakyMap — Apple Maps via MapKit JS with individually scattered dots.
 *
 * Manual clustering: at zoomed-out levels, dots are grouped by state into
 * cluster annotations with count. At zoomed-in levels, individual dots
 * with client names are shown. Zoom threshold is based on camera distance.
 *
 * Diff-based: annotations are only added/removed when visual key changes,
 * preventing cluster scatter/re-aggregate on SSE updates.
 */
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import type { IntakyStatusTier } from './intaky-tokens';
import {
  INTAKY_COLORS,
  INTAKY_FONT,
  INTAKY_STATE_COORDS,
  INTAKY_TIERS,
  statusToTier,
} from './intaky-tokens';

export interface IntakyMapIntake {
  id: string;
  firm_id?: string;
  client_display: string;
  status: string;
  state: string;
  city?: string;
  location_precision?: 'city' | 'state' | 'none';
  case_type: string;
  referral_source?: string;
  last_activity_headline: string;
  last_activity_detail?: string;
  last_activity_at?: string | null;
  rejection_reason?: string;
}

export interface MapTooltip {
  intakeId: string;
  headline: string;
  /** Rich detail text — note content, SMS body, etc. */
  detail?: string;
  tier: IntakyStatusTier;
  clientName: string;
  /** How many stacked updates for this intake (shown as badge) */
  stackCount?: number;
}

export interface IntakyMapHandle {
  getViewportOrigin: (intakeId: string) => { x: number; y: number } | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyMap = any;

declare global {
  interface Window {
    mapkit: AnyMap;
  }
}

interface IntakyMapProps {
  intakes: IntakyMapIntake[];
  tooltips: MapTooltip[];
  onDismissTooltip?: (intakeId: string) => void;
  onMapReady?: () => void;
  /** Override default map center [lat, lng] */
  initialCenter?: [number, number];
  /** Override default camera distance */
  initialDistance?: number;
  /** Override default map padding */
  mapPadding?: { top: number; right: number; bottom: number; left: number };
  /** If true, auto-fit the map to show all intakes after first load */
  autoFit?: boolean;
}

// ─── Jitter ─────────────────────────────────────────────────────────────────

function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function seededRandom(seed: number, index: number): number {
  // eslint-disable-next-line no-bitwise
  let s = ((seed + index * 2654435761) | 0) >>> 0;
  // eslint-disable-next-line no-bitwise
  s ^= s << 13;
  // eslint-disable-next-line no-bitwise
  s ^= s >> 17;
  // eslint-disable-next-line no-bitwise
  s ^= s << 5;
  // eslint-disable-next-line no-bitwise
  return (s >>> 0) / 4294967296;
}

/** Approximate radius multiplier per state — bigger states get wider jitter */
const STATE_SPREAD: Record<string, number> = {
  AK: 4.0, TX: 2.4, CA: 2.2, MT: 2.0, NM: 1.8, AZ: 1.8, NV: 1.6, CO: 1.5,
  OR: 1.5, WY: 1.5, KS: 1.4, NE: 1.4, SD: 1.4, ND: 1.4, MN: 1.3, MO: 1.2,
  OK: 1.2, WA: 1.2, IA: 1.1, IL: 1.1, WI: 1.1, GA: 1.1, FL: 1.1, MI: 1.1,
  // Small states get tighter radius
  CT: 0.3, DE: 0.25, RI: 0.2, DC: 0.15, NJ: 0.35, NH: 0.4, VT: 0.4, MA: 0.35,
  MD: 0.4, HI: 0.5,
};

function jitterCoord(
  baseLat: number,
  baseLng: number,
  intakeId: string,
  precision: 'city' | 'state',
  stateCode: string,
): [number, number] {
  const h = hashId(intakeId);
  const sizeMultiplier = STATE_SPREAD[stateCode] ?? 1.0;
  const baseRadius = precision === 'city' ? 0.5 : 1.2;
  const radius = baseRadius * sizeMultiplier;
  const angle = seededRandom(h, 0) * Math.PI * 2;
  const dist = Math.sqrt(seededRandom(h, 1)) * radius;
  return [baseLat + Math.cos(angle) * dist, baseLng + Math.sin(angle) * dist * 1.3];
}

function getIntakeCoord(intake: IntakyMapIntake): [number, number] | null {
  const precision = intake.location_precision ?? (intake.state ? 'state' : 'none');
  if (precision === 'none' || !intake.state || !INTAKY_STATE_COORDS[intake.state]) return null;
  const [baseLat, baseLng] = INTAKY_STATE_COORDS[intake.state];
  return jitterCoord(baseLat, baseLng, intake.id, precision, intake.state);
}

// ─── Zoom threshold ─────────────────────────────────────────────────────────

// Above this camera distance → show clusters. Below → show individual dots.
const CLUSTER_THRESHOLD = 5_000_000;

// ─── HTML builders ──────────────────────────────────────────────────────────

function pillHtml(text: string): string {
  return `<div style="margin-top:4px;background:white;border-radius:8px;
    height:20px;padding:0 8px;max-width:100px;
    display:flex;align-items:center;justify-content:center;
    font-size:10px;font-weight:700;color:#0F172A;
    font-family:Inter,system-ui,sans-serif;letter-spacing:0.02em;
    box-shadow:0 1px 4px rgba(0,0,0,0.22);white-space:nowrap;
    overflow:hidden;text-overflow:ellipsis;">${text}</div>`;
}

function individualDotHtml(tier: IntakyStatusTier, clientName: string, animate: boolean): string {
  const config = INTAKY_TIERS[tier];
  const dotSize = 14;
  const glow = tier === 'signed'
    ? `<div style="position:absolute;inset:-5px;border-radius:50%;background:${config.ring};pointer-events:none;"></div>`
    : '';
  const label = clientName.length > 12 ? `${clientName.slice(0, 11)}\u2026` : clientName;
  const anim = animate ? 'animation:intaky-dot-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-iteration-count:1;' : '';

  return `<div class="intaky-dot-hover" style="display:flex;flex-direction:column;align-items:center;cursor:pointer;${anim}">
    <div style="position:relative;width:${dotSize}px;height:${dotSize}px;
      display:flex;align-items:center;justify-content:center;">
      ${glow}
      <div class="intaky-dot-ring" style="position:absolute;inset:-18px;border-radius:50%;background:rgba(0,0,0,0.07);opacity:0;transition:opacity 0.2s;pointer-events:none;"></div>
      <div style="position:relative;width:${dotSize}px;height:${dotSize}px;border-radius:50%;
        background:${config.gradient};outline:2px solid white;outline-offset:-1px;
        box-shadow:0 2px 6px rgba(0,0,0,0.28),0 0 0 1px rgba(0,0,0,0.04);z-index:1;"></div>
    </div>
    ${pillHtml(label)}
  </div>`;
}

function clusterDotHtml(count: number, tier: IntakyStatusTier, stateCode: string, animate: boolean): string {
  const config = INTAKY_TIERS[tier];
  const size = Math.min(44, 20 + Math.sqrt(count) * 4);
  const anim = animate ? 'animation:intaky-dot-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-iteration-count:1;' : '';

  return `<div class="intaky-dot-hover" style="display:flex;flex-direction:column;align-items:center;cursor:pointer;${anim}">
    <div style="position:relative;width:${size}px;height:${size}px;
      display:flex;align-items:center;justify-content:center;">
      <div class="intaky-dot-ring" style="position:absolute;inset:-20px;border-radius:50%;background:rgba(0,0,0,0.07);opacity:0;transition:opacity 0.2s;pointer-events:none;"></div>
      <div style="position:relative;width:${size}px;height:${size}px;border-radius:50%;
        background:${config.gradient};outline:2.5px solid white;outline-offset:-1px;
        box-shadow:0 2px 8px rgba(0,0,0,0.30),0 0 0 1px rgba(0,0,0,0.04);
        z-index:1;display:flex;align-items:center;justify-content:center;
        font-size:${size > 30 ? 12 : 10}px;font-weight:700;color:white;
        font-family:Inter,system-ui,sans-serif;text-shadow:0 1px 2px rgba(0,0,0,0.3);">
        ${count}
      </div>
    </div>
    ${pillHtml(`${count} ${stateCode} lead${count === 1 ? '' : 's'}`)}
  </div>`;
}

// ─── Tier helpers ───────────────────────────────────────────────────────────

function findDominantTier(statuses: string[]): IntakyStatusTier {
  const priority: IntakyStatusTier[] = ['chase', 'new', 'signed', 'closed'];
  const tiers = statuses.map(statusToTier);
  return priority.find((t) => tiers.includes(t)) ?? 'new';
}

// ─── Annotation creation helpers ────────────────────────────────────────────

function makeAnnotation(
  mk: AnyMap,
  map: AnyMap,
  lat: number,
  lng: number,
  html: string,
  opts: { title?: string; subtitle?: string; data: Record<string, unknown> },
): AnyMap {
  const isCluster = !!opts.data.cluster;

  const annotation = new mk.Annotation(
    new mk.Coordinate(lat, lng),
    () => {
      const el = document.createElement('div');
      el.innerHTML = html;
      const root = el.firstElementChild as HTMLElement;

      // Cluster click → zoom in to that state
      if (isCluster) {
        root.style.cursor = 'pointer';
        root.addEventListener('click', (e: MouseEvent) => {
          e.stopPropagation();
          const st = opts.data.state as string;
          const stateCoord = INTAKY_STATE_COORDS[st];
          if (stateCoord) {
            const region = new mk.CoordinateRegion(
              new mk.Coordinate(stateCoord[0], stateCoord[1]),
              new mk.CoordinateSpan(4, 6),
            );
            map.setRegionAnimated(region, true);
          }
        });
      }

      return root;
    },
    {
      title: opts.title || ' ',
      subtitle: opts.subtitle || '',
      calloutEnabled: !isCluster,
      displayPriority: mk.Annotation.DisplayPriority.Required,
      data: opts.data,
    },
  );
  return annotation;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const IntakyMap = React.forwardRef<IntakyMapHandle, IntakyMapProps>(
  ({ intakes, tooltips, onDismissTooltip, onMapReady, initialCenter, initialDistance, mapPadding, autoFit }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<AnyMap>(null);
    // All current annotations on the map, keyed by a stable visual key
    const annotationsOnMap = useRef<Map<string, AnyMap>>(new Map());
    const unmappedCountRef = useRef<number>(0);
    const isClustered = useRef(true); // start clustered at US-wide zoom
    const isInitialRef = useRef(true);
    const onMapReadyRef = useRef(onMapReady);
    onMapReadyRef.current = onMapReady;
    const onDismissRef = useRef(onDismissTooltip);
    onDismissRef.current = onDismissTooltip;
    const token = import.meta.env.VITE_APP_APPLE_MAPS_TOKEN as string | undefined;

    const intakesRef = useRef(intakes);
    intakesRef.current = intakes;

    const [tooltipPositions, setTooltipPositions] = useState<
      Map<string, { x: number; y: number; tip: MapTooltip }>
    >(new Map());

    // ── Expose getViewportOrigin for confetti ──────────────────────────────
    useImperativeHandle(ref, () => ({
      getViewportOrigin: (intakeId: string) => {
        const map = mapRef.current;
        const mk = window.mapkit;
        if (!map || !mk) return null;
        const intake = intakesRef.current.find((i) => i.id === intakeId);
        if (!intake) return null;
        const coord = getIntakeCoord(intake);
        if (!coord) return null;
        const point = map.convertCoordinateToPointOnPage(new mk.Coordinate(coord[0], coord[1]));
        return { x: point.x / window.innerWidth, y: point.y / window.innerHeight };
      },
    }));

    // ── Build annotations based on zoom level ──────────────────────────────
    const renderAnnotations = useCallback((forceCluster?: boolean) => {
      const map = mapRef.current;
      const mk = window.mapkit;
      if (!map || !mk) return;

      const { current } = intakesRef;
      const clustered = forceCluster ?? isClustered.current;
      const isInitial = isInitialRef.current;
      isInitialRef.current = false;

      // Filter to mappable, non-closed intakes
      const mappable: IntakyMapIntake[] = [];
      let unmapped = 0;
      current.forEach((intake) => {
        const tier = statusToTier(intake.status);
        if (tier === 'closed') return;
        if (!getIntakeCoord(intake)) {
          unmapped += 1;
          return;
        }
        mappable.push(intake);
      });
      unmappedCountRef.current = unmapped;

      // Build target annotations
      const target = new Map<string, { lat: number; lng: number; html: string; title: string; subtitle: string; data: Record<string, unknown> }>();

      if (clustered) {
        // Group by state — single-lead states get an individual dot instead
        const byState: Record<string, IntakyMapIntake[]> = {};
        mappable.forEach((intake) => {
          (byState[intake.state] ??= []).push(intake);
        });
        Object.entries(byState).forEach(([st, list]) => {
          if (list.length === 1) {
            const intake = list[0];
            const [lat, lng] = INTAKY_STATE_COORDS[st] ?? [0, 0];
            const tier = statusToTier(intake.status);
            const key = `dot:${intake.id}:${tier}:${intake.client_display}`;
            target.set(key, {
              lat,
              lng,
              html: individualDotHtml(tier, intake.client_display, isInitial),
              title: intake.client_display,
              subtitle: intake.last_activity_headline || intake.case_type,
              data: { intakeId: intake.id, tier, state: st },
            });
          } else {
            const [lat, lng] = INTAKY_STATE_COORDS[st] ?? [0, 0];
            const dominant = findDominantTier(list.map((i) => i.status));
            // Key excludes dominant tier — status changes within a cluster
            // won't cause remove+add (which shifts the dot position)
            const key = `cluster:${st}:${list.length}`;
            target.set(key, {
              lat,
              lng,
              html: clusterDotHtml(list.length, dominant, st, isInitial),
              title: `${list.length} leads in ${st}`,
              subtitle: list.map((i) => i.client_display).join(', '),
              data: { cluster: true, state: st },
            });
          }
        });
      } else {
        // Individual dots
        mappable.forEach((intake) => {
          const coord = getIntakeCoord(intake);
          if (!coord) return;
          const tier = statusToTier(intake.status);
          const key = `dot:${intake.id}:${tier}:${intake.client_display}`;
          target.set(key, {
            lat: coord[0],
            lng: coord[1],
            html: individualDotHtml(tier, intake.client_display, isInitial),
            title: intake.client_display,
            subtitle: intake.last_activity_headline || intake.case_type,
            data: { intakeId: intake.id, tier, state: intake.state },
          });
        });
      }

      // Diff: remove stale first, then add new (atomic swap prevents overlap shift)
      const toRemove: AnyMap[] = [];
      annotationsOnMap.current.forEach((ann, key) => {
        if (!target.has(key)) {
          toRemove.push(ann);
          annotationsOnMap.current.delete(key);
        }
      });

      const toAdd: AnyMap[] = [];
      target.forEach((val, key) => {
        if (annotationsOnMap.current.has(key)) return;
        const ann = makeAnnotation(mk, map, val.lat, val.lng, val.html, {
          title: val.title,
          subtitle: val.subtitle,
          data: val.data,
        });
        annotationsOnMap.current.set(key, ann);
        toAdd.push(ann);
      });

      // Remove and add in one batch to minimize flicker
      if (toRemove.length) map.removeAnnotations(toRemove);
      if (toAdd.length) map.addAnnotations(toAdd);
    }, []);

    // ── Init map ───────────────────────────────────────────────────────────
    useEffect(() => {
      if (!containerRef.current) return undefined;

      const initApple = () => {
        if (!containerRef.current || mapRef.current) return;
        const mk = window.mapkit;
        mk.init({ authorizationCallback: (done: (t: string) => void) => done(token ?? '') });

        const center = initialCenter ?? [40.0, -97.5];
        const dist = initialDistance ?? 10_000_000;
        const pad = mapPadding ?? { top: 72, right: 0, bottom: Math.round(window.innerHeight * 0.52), left: 24 };

        const map = new mk.Map(containerRef.current, {
          center: new mk.Coordinate(center[0], center[1]),
          cameraDistance: dist,
          showsCompass: mk.FeatureVisibility.Hidden,
          showsZoomControl: false,
          showsMapTypeControl: false,
          showsScale: mk.FeatureVisibility.Hidden,
          isScrollEnabled: true,
          isZoomEnabled: true,
          isRotationEnabled: false,
          colorScheme: mk.Map.ColorSchemes.Light,
          mapType: mk.Map.MapTypes.Standard,
          showsPointsOfInterest: false,
          showsUserLocationControl: false,
          padding: new mk.Padding({
            top: pad.top,
            right: pad.right,
            bottom: pad.bottom,
            left: pad.left,
          }),
        });

        mapRef.current = map;

        // Click on any annotation
        map.addEventListener('select', (event: AnyMap) => {
          const { annotation } = event;
          if (!annotation?.data) return;

          if (annotation.data.cluster) {
            // Cluster click → zoom in to that state
            const stateCoord = INTAKY_STATE_COORDS[annotation.data.state as string];
            if (stateCoord) {
              // Use region-based zoom: ~4° span centered on state
              const region = new mk.CoordinateRegion(
                new mk.Coordinate(stateCoord[0], stateCoord[1]),
                new mk.CoordinateSpan(4, 6),
              );
              map.setRegionAnimated(region, true);
            }
            // Deselect so callout doesn't show
            try { map.selectedAnnotation = null; } catch { /* ok */ }
          }
        });

        // Listen for zoom changes to toggle cluster/individual mode
        map.addEventListener('region-change-end', () => {
          const newClustered = map.cameraDistance > CLUSTER_THRESHOLD;
          if (newClustered !== isClustered.current) {
            isClustered.current = newClustered;
            isInitialRef.current = true; // stagger animation on mode switch
            renderAnnotations(newClustered);
          }
        });

        if (intakesRef.current.length) {
          renderAnnotations(true);
        }

        onMapReadyRef.current?.();
      };

      if (window.mapkit) {
        initApple();
      } else {
        const existing = document.querySelector('script[data-intaky-mapkit]');
        if (!existing) {
          const script = document.createElement('script');
          script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
          script.dataset.intakyMapkit = '1';
          script.crossOrigin = 'anonymous';
          script.onload = initApple;
          document.head.appendChild(script);
        } else {
          existing.addEventListener('load', initApple);
        }
      }

      return () => {
        if (mapRef.current) {
          mapRef.current.destroy?.();
          mapRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Re-render annotations when intakes change ──────────────────────────
    const hasFittedRef = useRef(false);
    useEffect(() => {
      if (!mapRef.current) return;
      renderAnnotations();

      // Auto-fit: on first load with intakes, zoom to show all dots
      if (autoFit && !hasFittedRef.current && intakes.length > 0) {
        hasFittedRef.current = true;
        const mk = window.mapkit;
        if (!mk) return;
        const map = mapRef.current;

        // Collect unique states
        const states = new Set<string>();
        intakes.forEach((i) => {
          if (i.state && INTAKY_STATE_COORDS[i.state]) states.add(i.state);
        });

        if (states.size === 0) return;

        if (states.size === 1) {
          // Single state — zoom to it
          const st = [...states][0];
          const [lat, lng] = INTAKY_STATE_COORDS[st];
          const region = new mk.CoordinateRegion(
            new mk.Coordinate(lat, lng),
            new mk.CoordinateSpan(5, 7),
          );
          setTimeout(() => map.setRegionAnimated(region, true), 500);
        } else {
          // Multiple states — compute bounding box
          let minLat = 90;
          let maxLat = -90;
          let minLng = 180;
          let maxLng = -180;
          states.forEach((st) => {
            const [lat, lng] = INTAKY_STATE_COORDS[st];
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
          });
          const centerLat = (minLat + maxLat) / 2;
          const centerLng = (minLng + maxLng) / 2;
          const spanLat = Math.max(8, (maxLat - minLat) * 1.5);
          const spanLng = Math.max(10, (maxLng - minLng) * 1.5);
          const region = new mk.CoordinateRegion(
            new mk.Coordinate(centerLat, centerLng),
            new mk.CoordinateSpan(spanLat, spanLng),
          );
          setTimeout(() => map.setRegionAnimated(region, true), 500);
        }
      }
    }, [intakes, renderAnnotations, autoFit]);

    // ── Tooltip positioning — updates every frame to track the dot ──────────
    useEffect(() => {
      if (tooltips.length === 0) {
        setTooltipPositions(new Map());
        return undefined;
      }

      let raf: number;
      const updatePositions = () => {
        const map = mapRef.current;
        const mk = window.mapkit;
        if (!map || !mk) {
          raf = requestAnimationFrame(updatePositions);
          return;
        }
        const positions = new Map<string, { x: number; y: number; tip: MapTooltip }>();
        tooltips.forEach((tip) => {
          const intake = intakesRef.current.find((i) => i.id === tip.intakeId);
          if (!intake) return;
          let lat: number;
          let lng: number;
          if (isClustered.current) {
            const stateCoord = INTAKY_STATE_COORDS[intake.state];
            if (!stateCoord) return;
            [lat, lng] = stateCoord;
          } else {
            const coord = getIntakeCoord(intake);
            if (!coord) return;
            [lat, lng] = coord;
          }
          const point = map.convertCoordinateToPointOnPage(new mk.Coordinate(lat, lng));
          positions.set(tip.intakeId, { x: point.x, y: point.y, tip });
        });
        setTooltipPositions(positions);
        raf = requestAnimationFrame(updatePositions);
      };

      raf = requestAnimationFrame(updatePositions);
      return () => cancelAnimationFrame(raf);
    }, [tooltips]);

    // Auto-dismiss tooltips
    useEffect(() => {
      if (tooltips.length === 0) return undefined;
      const timers = tooltips.map((tip) =>
        setTimeout(() => onDismissRef.current?.(tip.intakeId), 4000),
      );
      return () => timers.forEach(clearTimeout);
    }, [tooltips]);

    const unmapped = unmappedCountRef.current;

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div style={{ width: '100%', height: '100%', filter: 'contrast(1.08) saturate(1.2)' }}>
          <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: '100vh' }} />
        </div>

        {/* ── DOM tooltips ── */}
        {Array.from(tooltipPositions.entries()).map(([id, { x, y, tip }]) => {
          const truncHeadline = tip.headline.length > 50
            ? `${tip.headline.slice(0, 49)}\u2026`
            : tip.headline;
          const truncDetail = tip.detail
            ? tip.detail.length > 80 ? `${tip.detail.slice(0, 79)}\u2026` : tip.detail
            : null;
          const stack = tip.stackCount ?? 0;

          return (
            <div
              key={id}
              style={{
                position: 'fixed',
                left: x,
                top: y - (truncDetail ? 90 : 70),
                transform: 'translateX(-50%)',
                zIndex: 50,
                pointerEvents: 'none',
                animation: 'intaky-tooltip-in 0.3s ease-out',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  background: 'white',
                  borderRadius: 12,
                  padding: '10px 14px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.08)',
                  fontFamily: INTAKY_FONT.family,
                  maxWidth: 240,
                  minWidth: 140,
                }}
              >
                {/* Stack count badge */}
                {stack > 1 && (
                  <div style={{
                    position: 'absolute', top: -6, right: -6,
                    width: 20, height: 20, borderRadius: '50%',
                    background: INTAKY_TIERS[tip.tier].dot, color: 'white',
                    fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}>
                    {stack}
                  </div>
                )}
                <div style={{ fontSize: 11, fontWeight: 700, color: INTAKY_COLORS.textPrimary, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tip.clientName}
                </div>
                <div style={{ fontSize: 11, fontWeight: 500, color: INTAKY_COLORS.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {truncHeadline}
                </div>
                {truncDetail && (
                  <div style={{
                    fontSize: 10, fontWeight: 400, color: INTAKY_COLORS.textMuted,
                    marginTop: 4, lineHeight: 1.4,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {truncDetail}
                  </div>
                )}
              </div>
              <div style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid white', margin: '0 auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }} />
            </div>
          );
        })}

      </div>
    );
  },
);

IntakyMap.displayName = 'IntakyMap';
